import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { messageService, eventOptionsService } from '../services/api';
import MessagesSidebar from '../components/MessagesSidebar';

const Messages = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const isRtl = language === 'fa';
    const t = (fa, en) => (isRtl ? fa : en);

    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filtersOpen, setFiltersOpen] = useState(true);

    const [eventOptions, setEventOptions] = useState([]);
    const [eventSlug, setEventSlug] = useState('');
    const [search, setSearch] = useState('');

    const fetchMessages = useCallback(
        async (params = {}) => {
            setLoading(true);
            setError(null);
            try {
                const data = await messageService.getMessages(params);
                setMessages(data || []);
            } catch (err) {
                console.error('Messages API error:', err);
                setError(t('خطا در دریافت پیام‌ها', 'Error fetching messages'));
            } finally {
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [language]
    );

    useEffect(() => {
        fetchMessages();

        const fetchEventOptions = async () => {
            try {
                const data = await eventOptionsService.getSubmittedEventSlugs();
                setEventOptions(data || []);
            } catch (err) {
                console.error('Event options fetch error:', err);
            }
        };
        fetchEventOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => fetchMessages({ eventSlug, search });

    const handleClearFilters = () => {
        setEventSlug('');
        setSearch('');
        fetchMessages({});
    };

    const formatDate = (isoString) => {
        if (!isoString) return '';
        try {
            return new Date(isoString).toLocaleDateString(isRtl ? 'fa-IR' : 'en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
        } catch {
            return isoString;
        }
    };

    return (
        <div className="max-w-[1136px] mx-auto px-3 sm:px-4 py-4 lg:py-8 font-iransansRegular overflow-hidden" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                <MessagesSidebar
                    activeKey="messages"
                    onNavigate={(key) => {
                        if (key === 'messages') navigate('/messages');
                        if (key === 'finance') navigate('/finance');
                        if (key === 'account') navigate('/account');
                        if (key === 'support') navigate('/support');
                        if (key === 'logout') navigate('/logout');
                    }}
                />

                <div className="flex-1 w-full min-w-0">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6 lg:mb-8 gap-3">
                        <motion.h1
                            className="text-xl sm:text-2xl lg:text-[32px] font-extrabold text-[#292DD8]"
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            {t('پیام‌ها', 'Messages')}
                        </motion.h1>

                        <motion.button
                            onClick={() => navigate('/messages/new')}
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            className="px-4 py-2 sm:px-6 sm:py-2.5 rounded-lg text-white font-bold text-xs sm:text-sm shadow-lg bg-gradient-to-r from-[#292DD8] to-[#4B4FEE] hover:from-[#1f23a8] hover:to-[#3a3ecc] transition-all duration-300 whitespace-nowrap"
                        >
                            {t('ارسال پیام جدید', 'New Message')}
                        </motion.button>
                    </div>

                    {/* Filters panel */}
                    <div className="bg-[#EBF1FB] rounded-2xl mb-5 overflow-hidden">
                        <button
                            onClick={() => setFiltersOpen((v) => !v)}
                            className="w-full flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4"
                        >
                            <span className="font-bold text-[#204286] text-sm sm:text-base">
                                {t('جست‌وجو پیام', 'Search Messages')}
                            </span>
                            <svg
                                className={`w-4 h-4 text-[#292DD8] transition-transform duration-300 ${filtersOpen ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        <AnimatePresence>
                            {filtersOpen && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="overflow-hidden"
                                >
                                    <div className="px-4 sm:px-5 pb-4 sm:pb-5 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                        <div>
                                            <label className="block text-xs sm:text-sm font-bold text-[#204286] mb-1.5">
                                                {t('رویداد', 'Event')}
                                            </label>
                                            <div className="relative">
                                                <select
                                                    value={eventSlug}
                                                    onChange={(e) => setEventSlug(e.target.value)}
                                                    className="w-full appearance-none bg-white border border-[#D6E1F5] rounded-lg px-3 py-2 text-xs sm:text-sm text-[#204286] focus:outline-none focus:ring-2 focus:ring-[#292DD8]/30"
                                                >
                                                    <option value="">{t('همه رویدادها', 'All events')}</option>
                                                    {eventOptions.map((opt) => (
                                                        <option key={opt.slug} value={opt.slug}>
                                                            {opt.title || opt.slug}
                                                        </option>
                                                    ))}
                                                </select>
                                                <svg
                                                    className={`w-3.5 h-3.5 absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-[#292DD8] pointer-events-none`}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs sm:text-sm font-bold text-[#204286] mb-1.5">
                                                {t('جست‌وجو در عنوان', 'Search by Title')}
                                            </label>
                                            <input
                                                type="text"
                                                placeholder={t('متن جست‌وجو', 'Search text')}
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                                className="w-full bg-white border border-[#D6E1F5] rounded-lg px-3 py-2 text-xs sm:text-sm text-[#204286] focus:outline-none focus:ring-2 focus:ring-[#292DD8]/30"
                                            />
                                        </div>
                                    </div>

                                    <div className="px-4 sm:px-5 pb-4 sm:pb-5 flex gap-3">
                                        <button
                                            onClick={handleSearch}
                                            className="px-5 py-2 rounded-lg text-white font-bold text-xs sm:text-sm bg-[#204286] hover:bg-[#16305f] transition-colors"
                                        >
                                            {t('جست‌وجو', 'Search')}
                                        </button>
                                        <button
                                            onClick={handleClearFilters}
                                            className="px-5 py-2 rounded-lg text-[#204286] font-bold text-xs sm:text-sm bg-white border border-[#D6E1F5] hover:bg-gray-50 transition-colors"
                                        >
                                            {t('پاک کردن فیلتر', 'Clear Filters')}
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Loading */}
                    {loading && (
                        <div className="flex justify-center items-center py-16">
                            <div className="w-10 h-10 border-4 border-[#292DD8] border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    )}

                    {/* Error */}
                    {!loading && error && (
                        <div className="flex flex-col items-center gap-3 bg-red-50 rounded-xl p-6">
                            <p className="text-red-600 font-bold text-sm">{error}</p>
                            <button
                                onClick={() => fetchMessages()}
                                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                            >
                                {t('تلاش مجدد', 'Try Again')}
                            </button>
                        </div>
                    )}

                    {/* Empty */}
                    {!loading && !error && messages.length === 0 && (
                        <div className="flex justify-center items-center py-16">
                            <p className="text-gray-500 text-sm sm:text-base">
                                {t('پیامی یافت نشد', 'No messages found')}
                            </p>
                        </div>
                    )}

                    {/* Table - desktop */}
                    {!loading && !error && messages.length > 0 && (
                        <>
                            <div className="hidden md:block overflow-hidden rounded-2xl border border-[#E5E5E5]">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-[#EBF1FB] text-[#204286] font-bold">
                                            <th className="px-4 py-3 text-right">{t('عنوان پیام', 'Title')}</th>
                                            <th className="px-4 py-3 text-center whitespace-nowrap">{t('رویداد', 'Event')}</th>
                                            <th className="px-4 py-3 text-center whitespace-nowrap">{t('تاریخ ارسال', 'Date Sent')}</th>
                                            <th className="px-4 py-3 text-center whitespace-nowrap">{t('فرستنده', 'Sender')}</th>
                                            <th className="px-4 py-3 text-center whitespace-nowrap">{t('عملیات', 'Actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {messages.map((msg, idx) => (
                                            <motion.tr
                                                key={msg.id}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.3, delay: idx * 0.04 }}
                                                className={`${idx % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFE]'} border-t border-[#EEF1F8]`}
                                            >
                                                <td className="px-4 py-3 text-[#204286] truncate max-w-[280px]">{msg.title}</td>
                                                <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap">
                                                    {msg.event?.title || '—'}
                                                </td>
                                                <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap">
                                                    {formatDate(msg.created_at)}
                                                </td>
                                                <td className="px-4 py-3 text-center whitespace-nowrap">
                                                    <span
                                                        className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                                                            msg.sender === 2
                                                                ? 'bg-blue-100 text-[#204286]'
                                                                : 'bg-gray-100 text-gray-600'
                                                        }`}
                                                    >
                                                        {msg.sender === 2 ? t('شما', 'You') : t('ادمین', 'Admin')}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center justify-center">
                                                        <button
                                                            onClick={() => navigate(`/messages/${msg.id}`)}
                                                            className="px-3 py-1.5 rounded-lg bg-[#204286] text-white text-xs font-bold hover:bg-[#16305f] transition-colors whitespace-nowrap"
                                                        >
                                                            {t('مشاهده پیام', 'View')}
                                                        </button>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Cards - mobile */}
                            <div className="md:hidden flex flex-col gap-3">
                                {messages.map((msg, idx) => (
                                    <motion.div
                                        key={msg.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: idx * 0.04 }}
                                        className="bg-white border border-[#EEF1F8] rounded-xl p-3.5 shadow-sm"
                                    >
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                            <p className="text-[#204286] text-sm font-medium line-clamp-2">{msg.title}</p>
                                            <span
                                                className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${
                                                    msg.sender === 2
                                                        ? 'bg-blue-100 text-[#204286]'
                                                        : 'bg-gray-100 text-gray-600'
                                                }`}
                                            >
                                                {msg.sender === 2 ? t('شما', 'You') : t('ادمین', 'Admin')}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                                            <span>{msg.event?.title || t('بدون رویداد', 'No event')}</span>
                                            <span>{formatDate(msg.created_at)}</span>
                                        </div>
                                        <button
                                            onClick={() => navigate(`/messages/${msg.id}`)}
                                            className="w-full px-3 py-1.5 rounded-lg bg-[#204286] text-white text-xs font-bold hover:bg-[#16305f] transition-colors"
                                        >
                                            {t('مشاهده پیام', 'View')}
                                        </button>
                                    </motion.div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Messages;